import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  disconnectIntegration,
  getAllRemoteDevicesList,
  getIntegrationCustomers,
  getIntegrationMappedCustomers,
  getIntegrationMappedProducts,
  getIntegrationMappedTax,
  getIntegrationSources,
  getRemoteDevicesList,
  mapIntegrationCustomers,
  mapIntegrationProduct,
  mapIntegrationTax,
  updateIntegrationStatus,
} from "controller/api";
import { ApiErrorType } from "shared";
import {
  GetRemoteDeviceListInput,
  IntegrationCustomerMappingInput,
  IntegrationProductMappingInput,
  IntegrationTaxMappingInput,
  UpdateIntegrationStatusInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

export const useGetIntegrationSources = (
  mine = false,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: ["integrationSources", mine],
    queryFn: () => getIntegrationSources(mine),
    enabled,
    onError: (err) => debugConsole(err),
  });
};

export const useUpdateIntegrationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdateIntegrationStatusInput) => updateIntegrationStatus(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["integrationSources"]);
      },
    },
  );
};

export const useDisconnectIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation((value: string) => disconnectIntegration(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["integrationSources"]);
      queryClient.invalidateQueries(["dattoAuthConfig"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "disconnectIntegration",
      });
    },
  });
};
export const useGetRemoteDevicesList = ({
  customerUserId,
  sourceName,
}: GetRemoteDeviceListInput) => {
  return useQuery({
    queryKey: ["remoteDevices", customerUserId, sourceName],
    queryFn: () => getRemoteDevicesList({ customerUserId, sourceName }),
    onError: (err) => debugConsole(err),
  });
};

export const useGetAllRemoteDevicesList = ({
  customerUserId,
  enabled = true,
}: {
  customerUserId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["remoteDevices", customerUserId],
    queryFn: () => getAllRemoteDevicesList({ customerUserId }),
    // The ticket's customer-user isn't known until the ticket loads.
    enabled: enabled && !!customerUserId,
    onError: (err) => debugConsole(err),
  });
};
export const useGetIntegrationCustomers = (source: string) => {
  return useQuery({
    queryKey: ["integrationIntegrationCustomer", source],
    queryFn: () => getIntegrationCustomers(source),
    onError: (err) => debugConsole(err),
  });
};

export const useGetIntegrationMappedCustomers = (
  source: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: ["integrationCustomers", source],
    queryFn: () => getIntegrationMappedCustomers(source),
    enabled,
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};

export const useMapIntegrationCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: IntegrationCustomerMappingInput) => mapIntegrationCustomers(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries([
          "integrationCustomers",
          variables.source,
        ]);
      },
    },
  );
};

export const useMapIntegrationTax = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: IntegrationTaxMappingInput) => mapIntegrationTax(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["integrationTax", variables.source]);
      },
    },
  );
};

export const useMapIntegrationProduct = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: IntegrationProductMappingInput) => mapIntegrationProduct(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["integrationProduct", variables.source]);
      },
    },
  );
};

export const useGetIntegrationMappedTax = (source: string) => {
  return useQuery({
    queryKey: ["integrationTax", source],
    queryFn: () => getIntegrationMappedTax(source),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};

export const useGetIntegrationMappedProduct = (source: string) => {
  return useQuery({
    queryKey: ["integrationProduct", source],
    queryFn: () => getIntegrationMappedProducts(source),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};
